import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { useChatDock } from "../hooks/use-chat-dock";
import { DOCK_SHORTCUT_LABEL } from "../utils/shortcut";

/**
 * Dock kapalıyken ekranın sağ kenarına yapışan tutamaç.
 *
 * Dikey ortada durur, aktif modülün rengini alır. Kolon toplanınca sağdan
 * kayarak gelir (`dock-handle-in`); hover'da genişleyip durumunu söyler:
 * "Asistan" / "Yanıt yazılıyor" / "Yeni yanıt". Arka planda akan bir yanıt
 * varsa nabız, okunmamış yanıt varsa nokta gösterir — sohbet stream
 * store'da yaşadığı için dock kapalıyken de sürer.
 */
export const ChatDockToggle: React.FC = () => {
  const { t } = useTranslation();
  const { isAvailable, isOpen, open, module, isStreaming, hasUnread } =
    useChatDock();

  if (!isAvailable || isOpen || !module) return null;
  const Icon = module.icon;

  const statusLabel = hasUnread
    ? t("chatDock.unread")
    : isStreaming
      ? t("chatDock.streaming")
      : t("chatDock.title");
  const ariaLabel = hasUnread
    ? `${t("chatDock.open")} — ${t("chatDock.unread")}`
    : t("chatDock.open");

  return (
    <button
      type="button"
      onClick={open}
      aria-label={ariaLabel}
      title={`${t("chatDock.open")} · ${DOCK_SHORTCUT_LABEL}`}
      className={cn(
        "group fixed right-0 top-1/2 z-[945] -translate-y-1/2",
        "flex items-center rounded-l-xl py-3 pl-2.5 pr-1.5",
        "bg-module-accent text-module-accent-foreground shadow-lg shadow-black/15",
        "transition-[padding,box-shadow] duration-200 hover:pl-3 hover:shadow-xl hover:shadow-module-accent/30",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-module-accent/50",
        // Kolon toplanırken değil, toplandıktan hemen sonra belirsin
        "animate-[dock-handle-in_0.4s_cubic-bezier(0.22,1,0.36,1)_0.22s_both] motion-reduce:animate-none",
      )}
    >
      <span className="relative flex shrink-0">
        <Icon size={18} className="size-[18px]" />
        {isStreaming && !hasUnread && (
          <span
            aria-hidden
            className="absolute -right-1.5 -top-1.5 size-2.5 rounded-full bg-white"
          >
            <span className="absolute inset-0 animate-ping rounded-full bg-white/80" />
          </span>
        )}
        {hasUnread && (
          <span
            aria-hidden
            className="absolute -right-1.5 -top-1.5 size-2.5 rounded-full bg-white ring-2 ring-module-accent"
          />
        )}
      </span>

      {/* Hover'da açılan durum etiketi */}
      <span
        className={cn(
          "max-w-0 overflow-hidden whitespace-nowrap text-sm font-medium opacity-0",
          "transition-[max-width,opacity,margin] duration-300 ease-out",
          "group-hover:ml-2 group-hover:max-w-[180px] group-hover:opacity-100",
          "group-focus-visible:ml-2 group-focus-visible:max-w-[180px] group-focus-visible:opacity-100",
        )}
      >
        {statusLabel}
      </span>

      <ChevronLeft className="ml-1 size-4 opacity-80 transition-transform duration-200 group-hover:-translate-x-0.5" />
    </button>
  );
};
